import { Cookies } from 'typescript-cookie';
import { getWindowData } from '../../FitAssistantShared/GetWindowData.js';

export type Cookie = {
  cookie: string;
  perCategoryCookie: string;
};

export type CookieNames = {
  cookieName: string;
  categoriesCookieName: string;
  legacyPerCategoryCookieName: string;
};

type CategorySelections = Record<string, string>;

// Stay below the 4096 byte cookie limit
const MAX_CATEGORIES_COOKIE_LENGTH = 3500;

export class CookieHelper {
  public static GetCookies(): Cookie {
    let cookieNames = this.GetCookieNames();

    return {
      cookie: Cookies.get(cookieNames.cookieName) as string,
      perCategoryCookie: this.GetPerCategorySelection(cookieNames) as string,
    };
  }
  public static SetCookies(selection: string, perCategorySelection: string): void {
    let cookieNames = this.GetCookieNames();

    Cookies.set(cookieNames.cookieName, selection, {
      expires: 365,
      path: this.GetLanguagePath(),
      secure: true,
      sameSite: 'strict',
    });

    let selections = this.GetCategorySelections(cookieNames);
    delete selections[this.GetCategoryId()];
    selections[this.GetCategoryId()] = perCategorySelection;
    this.SetCategorySelections(cookieNames, selections);
  }
  public static RemoveCookies(): void {
    let cookieNames = this.GetCookieNames();

    Cookies.remove(cookieNames.cookieName, {
      path: this.GetLanguagePath(),
    });

    let selections = this.GetCategorySelections(cookieNames);
    delete selections[this.GetCategoryId()];
    this.SetCategorySelections(cookieNames, selections);

    this.RemoveLegacyPerCategoryCookie(cookieNames);
  }

  public static GetCookieNames(): CookieNames {
    return {
      cookieName: window._THULEDATA.FitAssistant.FitAssistantCookieName,
      categoriesCookieName: window._THULEDATA.FitAssistant.FitAssistantCategoriesCookieName,
      legacyPerCategoryCookieName: window._THULEDATA.FitAssistant.FitAssistantLegacyPerCategoryCookieName,
    };
  }

  private static GetCategoryId(): string {
    return window._THULEDATA.FitAssistant.CategoryId;
  }

  private static GetLanguagePath(): string {
    return `/${getWindowData().SitecoreLanguage.toLowerCase()}/`;
  }

  private static GetPerCategorySelection(cookieNames: CookieNames): string | undefined {
    let selections = this.GetCategorySelections(cookieNames);
    let categoryId = this.GetCategoryId();

    if (categoryId in selections) return selections[categoryId];

    let legacySelection = this.GetLegacyPerCategorySelection(cookieNames);

    if (legacySelection === undefined) return undefined;

    selections[categoryId] = legacySelection;
    this.SetCategorySelections(cookieNames, selections);
    this.RemoveLegacyPerCategoryCookie(cookieNames);

    return legacySelection;
  }

  private static GetCategorySelections(cookieNames: CookieNames): CategorySelections {
    let value = Cookies.get(cookieNames.categoriesCookieName);

    if (typeof value !== 'string') return {};

    try {
      let selections = JSON.parse(value);
      return selections && typeof selections === 'object' && !Array.isArray(selections) ? selections : {};
    } catch {
      return {};
    }
  }

  private static SetCategorySelections(cookieNames: CookieNames, selections: CategorySelections): void {
    let categoryIds = Object.keys(selections);

    while (
      categoryIds.length > 1 &&
      encodeURIComponent(JSON.stringify(selections)).length > MAX_CATEGORIES_COOKIE_LENGTH
    ) {
      delete selections[categoryIds.shift() as string];
    }

    if (categoryIds.length === 0) {
      Cookies.remove(cookieNames.categoriesCookieName, { path: this.GetLanguagePath() });
      return;
    }

    Cookies.set(cookieNames.categoriesCookieName, JSON.stringify(selections), {
      expires: 365,
      path: this.GetLanguagePath(),
      secure: true,
      sameSite: 'strict',
    });
  }

  private static GetLegacyPerCategoryPath(): string {
    return window.location.pathname
      .split('/')
      .filter((x) => !x.includes('-_-'))
      .join('/');
  }

  private static GetLegacyPerCategorySelection(cookieNames: CookieNames): string | undefined {
    Cookies.remove(cookieNames.legacyPerCategoryCookieName, {
      path: this.GetLanguagePath().replace(/\/$/, ''),
    });

    if (this.GetLegacyPerCategoryPath().split('/').filter(Boolean).length < 2) return undefined;

    return Cookies.get(cookieNames.legacyPerCategoryCookieName) as string | undefined;
  }

  private static RemoveLegacyPerCategoryCookie(cookieNames: CookieNames): void {
    Cookies.remove(cookieNames.legacyPerCategoryCookieName, {
      path: this.GetLegacyPerCategoryPath(),
    });
  }
}
